import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from 'Components/Alert';
import CheckInput from 'Components/Form/CheckInput';
import FormGroup from 'Components/Form/FormGroup';
import FormLabel from 'Components/Form/FormLabel';
import SelectInput from 'Components/Form/SelectInput';
import TextInput from 'Components/Form/TextInput';
import Button from 'Components/Link/Button';
import ConfirmModal from 'Components/Modal/ConfirmModal';
import Table from 'Components/Table/Table';
import TableBody from 'Components/Table/TableBody';
import TableRow from 'Components/Table/TableRow';
import TableRowCell from './TdarrTableRowCell';
import { kinds } from 'Helpers/Props';
import { tdarrCrud, tdarrPost } from '../api';
import { TdarrLibrary } from '../types';

interface StackItem {
  _id: string;
  id: string;
  checked: boolean;
  source: string;
  priority: number;
  InputsDB?: Record<string, unknown>;
  [key: string]: unknown;
}

interface PluginInput {
  name: string;
  defaultValue?: unknown;
  inputUI?: { type?: string; options?: Array<string | { label: string; value: string }> };
}

interface PluginMetadata {
  id: string;
  Name?: string;
  Type?: string;
  Operation?: string;
  Stage?: string;
  Description?: string;
  source?: string;
  Inputs?: PluginInput[];
}

const columns = [
  { name: 'enabled', label: 'Enabled', isVisible: true },
  { name: 'plugin', label: 'Plugin', isVisible: true },
  { name: 'source', label: 'Source', isVisible: true },
  { name: 'type', label: 'Type', isVisible: true },
  { name: 'operation', label: 'Operation', isVisible: true },
  { name: 'inputs', label: 'Inputs', isVisible: true },
  { name: 'priority', label: 'Priority', isVisible: true },
  { name: 'actions', label: 'Actions', isVisible: true }
];

const catalogueColumns = [
  { name: 'name', label: 'Plugin', isVisible: true },
  { name: 'type', label: 'Type', isVisible: true },
  { name: 'operation', label: 'Operation', isVisible: true },
  { name: 'source', label: 'Source', isVisible: true },
  { name: 'actions', label: 'Actions', isVisible: true }
];

type Change =
  | { type: 'enabled'; item: StackItem; value: boolean }
  | { type: 'remove'; item: StackItem }
  | { type: 'add'; plugin: PluginMetadata }
  | { type: 'move'; item: StackItem; direction: -1 | 1 }
  | { type: 'inputs'; item: StackItem };

export default function LibraryPluginStack({ library, onMessage }: { library: TdarrLibrary; onMessage: (message: { kind: 'success' | 'danger'; text: string }) => void }) {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  const [source, setSource] = useState('Community');
  const [removeId, setRemoveId] = useState('');
  const [inputDrafts, setInputDrafts] = useState<Record<string, Record<string, string>>>({});
  const stack = useMemo(() => (Array.isArray(library.pluginIDs) ? library.pluginIDs as StackItem[] : []).slice().sort((a, b) => a.priority - b.priority), [library.pluginIDs]);
  const catalogue = useQuery({
    queryKey: ['tdarr', 'plugins', 'catalogue'],
    queryFn: async () => {
      const [community, local] = await Promise.all([
        tdarrPost<[PluginMetadata[], string]>('/search-plugins', { data: { string: '', pluginType: 'Community' } }),
        tdarrPost<[PluginMetadata[], string]>('/search-plugins', { data: { string: '', pluginType: 'Local' } })
      ]);
      return [...(community[0] ?? []), ...(local[0] ?? [])];
    },
    staleTime: 5 * 60 * 1000
  });
  const metadata = useMemo(() => new Map((catalogue.data ?? []).map((plugin) => [`${plugin.source}:${plugin.id}`, plugin])), [catalogue.data]);
  const results = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return [];
    return (catalogue.data ?? []).filter((plugin) => (plugin.source ?? 'Community') === source && `${plugin.id} ${plugin.Name ?? ''} ${plugin.Description ?? ''} ${plugin.Type ?? ''} ${plugin.Operation ?? ''}`.toLowerCase().includes(needle)).slice(0, 100);
  }, [catalogue.data, source, term]);
  useEffect(() => {
    setInputDrafts(Object.fromEntries(stack.map((item) => {
      const plugin = metadata.get(`${item.source}:${item.id}`);
      return [item._id, Object.fromEntries((plugin?.Inputs ?? []).map((input) => [input.name, String(item.InputsDB?.[input.name] ?? input.defaultValue ?? '')]))];
    })));
  }, [metadata, stack]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tdarr', 'collection', 'LibrarySettingsJSONDB'] });
  const mutate = useMutation({
    mutationFn: async (change: Change) => {
      if (change.type === 'enabled') return tdarrPost('/update-plugin-include', { data: { dbID: library._id, ele: change.item._id, status: change.value } });
      if (change.type === 'remove') return tdarrPost('/remove-plugin-include', { data: { dbID: library._id, ele: change.item._id } });
      if (change.type === 'add') return tdarrPost('/add-plugin-include', { data: { dbID: library._id, ele: change.plugin.id, source: change.plugin.source ?? source, index: stack.length } });
      const index = stack.findIndex((item) => item._id === change.item._id);
      if (index < 0) return;
      const next = stack.map((item) => ({ ...item, InputsDB: { ...(item.InputsDB ?? {}) } }));
      if (change.type === 'move') {
        const target = index + change.direction;
        if (target < 0 || target >= next.length) return;
        const [item] = next.splice(index, 1);
        next.splice(target, 0, item);
      } else {
        next[index].InputsDB = { ...inputDrafts[change.item._id] };
      }
      return tdarrCrud({ collection: 'LibrarySettingsJSONDB', mode: 'update', docID: library._id, obj: { pluginIDs: next.map((item, priority) => ({ ...item, priority })) } });
    },
    onSuccess: async (_data, change) => {
      setRemoveId('');
      await refresh();
      onMessage({ kind: 'success', text: change.type === 'add' ? 'Plugin added to stack.' : change.type === 'remove' ? 'Plugin removed from stack.' : 'Plugin stack updated.' });
    },
    onError: () => { setRemoveId(''); onMessage({ kind: 'danger', text: 'Plugin stack could not be updated.' }); }
  });
  const selectedForRemoval = stack.find((item) => item._id === removeId);
  return <>
    <Alert>Stack changes are applied immediately. Plugin inputs are saved with the Save Inputs button.</Alert>
    {catalogue.isError && <Alert kind={kinds.DANGER}>Plugin metadata could not be loaded.</Alert>}
    {!stack.length ? <Alert>No classic plugins are configured for this library.</Alert> : <Table columns={columns}><TableBody>{stack.map((item, index) => {
      const plugin = metadata.get(`${item.source}:${item.id}`);
      return <TableRow key={item._id}>
        <TableRowCell><CheckInput name={`enabled-${item._id}`} value={item.checked} isDisabled={mutate.isPending} onChange={({ value }) => mutate.mutate({ type: 'enabled', item, value: Boolean(value) })} /></TableRowCell>
        <TableRowCell><strong>{plugin?.Name ?? item.id}</strong><br /><small>{item.id}</small></TableRowCell>
        <TableRowCell>{item.source}</TableRowCell>
        <TableRowCell>{plugin?.Type ?? '—'}</TableRowCell>
        <TableRowCell>{plugin?.Operation ?? '—'}{plugin?.Stage ? ` · ${plugin.Stage}` : ''}</TableRowCell>
        <TableRowCell>{plugin?.Inputs?.length ? <div>{plugin.Inputs.map((input) => {
          const value = inputDrafts[item._id]?.[input.name] ?? '';
          const options = input.inputUI?.type === 'dropdown' ? (input.inputUI.options ?? []).map((option) => typeof option === 'string' ? { key: option, value: option } : { key: option.value, value: option.label }) : [];
          return <FormGroup key={input.name}><FormLabel name={`${item._id}-${input.name}`}>{input.name}</FormLabel>{options.length ? <SelectInput<string> name={`${item._id}-${input.name}`} value={value} values={options} onChange={({ value: next }) => setInputDrafts((current) => ({ ...current, [item._id]: { ...current[item._id], [input.name]: next } }))} /> : <TextInput name={`${item._id}-${input.name}`} value={value} onChange={({ value: next }) => setInputDrafts((current) => ({ ...current, [item._id]: { ...current[item._id], [input.name]: String(next) } }))} />}</FormGroup>;
        })}<Button size="small" isDisabled={mutate.isPending} onPress={() => mutate.mutate({ type: 'inputs', item })}>Save Inputs</Button></div> : '—'}</TableRowCell>
        <TableRowCell>{index + 1}</TableRowCell>
        <TableRowCell><Button size="small" isDisabled={index === 0 || mutate.isPending} onPress={() => mutate.mutate({ type: 'move', item, direction: -1 })}>Up</Button>{' '}<Button size="small" isDisabled={index === stack.length - 1 || mutate.isPending} onPress={() => mutate.mutate({ type: 'move', item, direction: 1 })}>Down</Button>{' '}<Button size="small" kind={kinds.DANGER} isDisabled={mutate.isPending} onPress={() => setRemoveId(item._id)}>Remove</Button></TableRowCell>
      </TableRow>;
    })}</TableBody></Table>}
    <FormGroup><FormLabel name="pluginSource">Plugin Source</FormLabel><SelectInput<string> name="pluginSource" value={source} values={[{ key: 'Community', value: 'Community' }, { key: 'Local', value: 'Local' }]} onChange={({ value }) => setSource(value)} /></FormGroup>
    <FormGroup><FormLabel name="pluginSearch">Add Plugin</FormLabel><TextInput name="pluginSearch" value={term} placeholder="Search by name, identifier, codec, or operation" onChange={({ value }) => setTerm(String(value))} /></FormGroup>
    {term.trim() && !catalogue.isLoading && !results.length && <Alert>No matching plugins found.</Alert>}
    {!!results.length && <Table columns={catalogueColumns}><TableBody>{results.map((plugin) => {
      const alreadyAdded = stack.some((item) => item.id === plugin.id);
      return <TableRow key={`${plugin.source}-${plugin.id}`}><TableRowCell><strong>{plugin.Name ?? plugin.id}</strong><br /><small>{plugin.id}</small></TableRowCell><TableRowCell>{plugin.Type ?? '—'}</TableRowCell><TableRowCell>{plugin.Operation ?? '—'}</TableRowCell><TableRowCell>{plugin.source ?? source}</TableRowCell><TableRowCell><Button size="small" kind={kinds.PRIMARY} isDisabled={alreadyAdded || mutate.isPending} onPress={() => mutate.mutate({ type: 'add', plugin })}>{alreadyAdded ? 'In Stack' : 'Add'}</Button></TableRowCell></TableRow>;
    })}</TableBody></Table>}
    <ConfirmModal isOpen={Boolean(removeId)} kind="danger" title="Remove Plugin" message={`Remove ${metadata.get(`${selectedForRemoval?.source}:${selectedForRemoval?.id}`)?.Name ?? selectedForRemoval?.id ?? removeId} from this library's stack?`} confirmLabel="Remove" isSpinning={mutate.isPending} onConfirm={() => selectedForRemoval && mutate.mutate({ type: 'remove', item: selectedForRemoval })} onCancel={() => setRemoveId('')} />
  </>;
}
